// The account's word for a community ("Association" at some customers), from shell/status `terms`. Every
// user-visible "community" reads it; routes, API fields and code identifiers keep "community". ShellStatusProvider
// sets it when the status loads; components call useTerms() so they re-render, other modules call terms() at call time.

export const DEFAULT_TERMS = Object.freeze({ community: 'Community', communities: 'Communities' });

const WORD = /^\p{L}+( \p{L}+)*$/u;

export function lowerTerm(term) {
  return String(term || '')
    .split(' ')
    .map((word) => (word.length > 1 && word.toUpperCase() === word ? word : word.toLowerCase()))
    .join(' ');
}

/** The server's plural guess for a blank plural: "Association" → "Associations", "Property" → "Properties". */
export function pluralTerm(singular) {
  const text = String(singular || '').trim();
  if (!text) return '';
  if (/[^aeiou]y$/i.test(text)) return `${text.slice(0, -1)}ies`;
  if (/(s|x|z|ch|sh)$/i.test(text)) return `${text}es`;
  return `${text}s`;
}

function article(word) {
  return /^[aeiou]/i.test(word) ? 'an' : 'a';
}

export function describeTerms(raw) {
  const community = validTerm(raw?.community) || DEFAULT_TERMS.community;
  const communities = validTerm(raw?.communities) || pluralTerm(community);
  const communityLower = lowerTerm(community);
  const communitiesLower = lowerTerm(communities);
  return Object.freeze({
    community,
    communities,
    communityLower,
    communitiesLower,
    aCommunity: `${article(community)} ${communityLower}`,
    ACommunity: `${article(community) === 'an' ? 'An' : 'A'} ${communityLower}`,
    isDefault: community === DEFAULT_TERMS.community && communities === DEFAULT_TERMS.communities,
    count: (n) => `${n} ${n === 1 ? communityLower : communitiesLower}`,
  });
}

function validTerm(value) {
  const text = String(value || '')
    .trim()
    .replace(/\s+/g, ' ');
  return text && text.length <= 40 && WORD.test(text) ? text : null;
}

let current = describeTerms(DEFAULT_TERMS);

/** Sets the account's terms (from shell/status) and returns the described terms. */
export function setAccountTerms(raw) {
  const next = describeTerms(raw);
  if (next.community !== current.community || next.communities !== current.communities) current = next;
  return current;
}

/** The account's terms right now, for code outside components (export columns, logic modules). */
export function terms() {
  return current;
}

/** Client-side check matching the server: 1–40 letters and spaces. Returns an error message or null. */
export function termError(value, which) {
  const text = String(value || '')
    .trim()
    .replace(/\s+/g, ' ');
  if (!text) return which === 'singular' ? 'Enter a word, for example Community or Association.' : null;
  if (text.length > 40) return 'Use at most 40 characters.';
  if (!WORD.test(text)) return 'Use letters and spaces only.';
  return null;
}
